import { formatCueTime, formatWindowTime, MIN_CUE_MS } from "providers/AudioProvider/editorErrors";
import {
    addCueWindow,
    cueTimingBroken,
    pairTimingOf,
    pairTimingProblem,
    pairWindowBroken,
    PairTiming,
    restoreWindow,
    synthTimingRefusal,
    unplayableVoicings,
    VoicingType,
} from "providers/AudioProvider/cueTiming";
import { AudioTrack } from "providers/AudioProvider/types";
import { voicingCheckWarning } from "providers/AudioProvider/voicingCheck";

export {
    addCueWindow,
    cueTimingBroken,
    formatWindowTime,
    MIN_CUE_MS,
    pairTimingOf,
    pairTimingProblem,
    pairWindowBroken,
    restoreWindow,
    synthTimingRefusal,
    unplayableVoicings,
};

const at = (ms: number | null | undefined) => (ms === null || ms === undefined ? "?" : formatCueTime(ms));

/**
 * Почему ♪ (tts) или 🔊 (original_ru) сейчас не может озвучить реплику — то, на что сервер
 * заведомо ответит отказом, и только это (зеркало synth_refusal, translate-python): пары нет;
 * для ♪ — пустой текст (оригинальному звуку текст не нужен); отрицательные место или слот; для
 * 🔊 — ещё конец слота не позже места. Длина перевода не в счёт: её пересчитывает переозвучка.
 * `timing` не известно (строка собрана не из пары) — время оставляется серверу. null — можно.
 */
export const voicingBlockReason = ({
    hasPair,
    text,
    type = "tts",
    timing,
    en,
}: {
    hasPair: boolean;
    text: string | undefined;
    type?: VoicingType;
    timing?: PairTiming;
    en: boolean;
}): string | null => {
    if (!hasPair) {
        return en
            ? "This cue has no translation on the server yet — type the translation or press →"
            : "У реплики нет перевода на сервере — введите текст перевода или нажмите →";
    }
    if (type !== "original_ru" && (!text || !text.trim())) {
        return en ? "The translation is empty — nothing to voice" : "Текст перевода пуст — озвучивать нечего";
    }
    const refusal = timing ? synthTimingRefusal(timing, type) : null;
    switch (refusal) {
        case "start":
            return en
                ? `The voicing is placed before the folder's start (${at(timing!.start)}) — restore the cue's window first`
                : `Озвучка поставлена раньше начала папки (${at(timing!.start)}) — сначала верните реплике окно`;
        case "slot_start":
            return en
                ? `The translation's start is broken (${at(timing!.slotStart)}) — restore the cue's window first`
                : `Начало перевода испорчено (${at(timing!.slotStart)}) — сначала верните реплике окно`;
        case "slot_end":
            return en
                ? `The translation's end is broken (${at(timing!.slotEnd)}) — restore the cue's window first`
                : `Конец перевода испорчен (${at(timing!.slotEnd)}) — сначала верните реплике окно`;
        case "order":
            return en
                ? `Original sound: the cue's end (${at(timing!.slotEnd)}) is not after the voicing's place (${at(timing!.start)}) — nothing to cut`
                : `Оригинальный звук: конец реплики (${at(timing!.slotEnd)}) не позже места озвучки (${at(timing!.start)}) — вырезать нечего`;
        default:
            return null;
    }
};

/** То же для строки дорожки оригинала: пару и её время строка несёт с собой (updateRusData). */
export const rowVoicingBlockReason = (track: AudioTrack, type: VoicingType, en: boolean): string | null => {
    const known = track.has_translation === true && track.translation_start !== undefined;
    const timing: PairTiming | undefined = known
        ? {
              start: track.translation_start ?? null,
              slotEnd: track.translation_slot_end ?? null,
              ...(track.translation_slot_start !== undefined ? { slotStart: track.translation_slot_start } : {}),
          }
        : undefined;
    // Снятая с озвучки реплика (⊘): новый сервер хранит её текст в `skipped_captions`, а ♪ сначала
    // возвращает её с этим текстом — «пустым» такой перевод для ♪ не считается.
    const text =
        track.translation_skip === true && !(track.translation || "").trim()
            ? track.translation_skipped_text
            : track.translation;
    return voicingBlockReason({
        hasPair: track.has_translation ?? track.translation !== undefined,
        text,
        type,
        timing,
        en,
    });
};

/**
 * Предупреждение ♪ строки: сервер прослушал дубль короткой реплики и не услышал её текста даже
 * после всех попыток («озвучка не совпала с текстом: слышно «खारे»»). null — нечего сказать.
 */
export const rowVoicingCheckWarning = (track: AudioTrack, en: boolean): string | null =>
    voicingCheckWarning(track.translation_voicing_check, en);
